/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import axios from "axios";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { ErrorMessage } from "@hookform/error-message";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "../../components/MinimalSelect";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import theme from "../../styles/theme";

import { StyledText } from "../../components/Text";
import Input from "../../components/Input";

import Button from "../../components/Button";
import { RightAlign } from "../../components/UtilsComponents";
import Icon, { ClickableIcon } from "../../components/Icon";
import Status from "../../types/enum/Status";
import Modal from "../../components/Modal";
import { Switch } from "@mui/material";
import moment from "moment";

const sharedMinimalStyle = css`å
  width: 100%;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 12px;
  .react-select__menu {
    width: 100%;
    min-width: unset;
    margin-left: -12px;
  }
`;
const StyledMinimalSelect = styled(MinimalSelect)`
  ${sharedMinimalStyle}
`;
type InstructorForm = {
  cmkl_email: string | undefined;
  first_name: string | undefined;
  last_name: string | undefined;
  id: string | undefined;
};
type TermEditModalProps = {
  isOpen?: boolean;
  setOpen?: (x: boolean) => void;
  instructorData: {
    cmkl_email: string | undefined;
    first_name: string | undefined;
    last_name: string | undefined;
    id: string | undefined;
  };
  onEditComplete?: () => void;
  fetchInstructors?: () => void;
  setMessage: (message: string) => void;
  setEditInstructor: (x: any) => void;
  setOpenSuccess: (x: boolean) => void;
  setOpenError: (x: boolean) => void;
};

const EditInstructorModal = ({
  isOpen,
  setOpen,
  instructorData,
  setEditInstructor,
  setMessage,
  setOpenSuccess,
  setOpenError,
  fetchInstructors,
}: TermEditModalProps): JSX.Element => {
  const validationSchema = Yup.object({
    cmkl_email: Yup.string().required("Email is required"),
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
  });
  const {
    register,
    handleSubmit,

    formState: { errors, isDirty },
  } = useForm<InstructorForm>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      cmkl_email: instructorData.cmkl_email,
      first_name: instructorData.first_name,
      last_name: instructorData.last_name,
    },
  });

  const [loading, setLoading] = useState<boolean>(false);

  const onSubmitClicked = (data: any) => {
    setLoading(true);
    const editData = {
      cmkl_email: data.cmkl_email,
      first_name: data.first_name,
      last_name: data.last_name,
      user_id: instructorData.id,
    };
    axios
      .put(`/api/v1/instructor`, editData)
      .then((res) => {
        setLoading(false);
        setMessage(res.data.data.message);
        setOpenSuccess(true);
        setOpen?.(false);
        setEditInstructor(null);
        fetchInstructors && fetchInstructors();
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setOpenError(true);
        setLoading(false);
      });
  };
  const onErrorCloseModal = () => {
    setOpenError(false);
  };
  const onError = (errors: any, e: any) => console.log("error", errors, e);

  return (
    <>
      <Modal
        isOpen={!!isOpen}
        setOpen={setOpen}
        onClose={() => {
          setOpen?.(false);
          setEditInstructor(null);
        }}
      >
        <FlexBox
          style={{
            marginBottom: theme.spacing.base * 3,
          }}
        >
          <FlexGrow>
            <h3 style={{ marginRight: "auto", marginBottom: 0 }}>
              Edit Instructor
            </h3>
          </FlexGrow>
          <RightAlign>
            <ClickableIcon
              name="x-s"
              onClick={() => {
                setOpen?.(false);
                setEditInstructor(null);
              }}
            />
          </RightAlign>
        </FlexBox>
        {!loading ? (
          <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  First Name
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <Input {...register("first_name")} isFullWidth />
                  <ErrorMessage
                    errors={errors}
                    name="first_name"
                    render={({ message }) => (
                      <p
                        style={{
                          marginTop: 0,
                          color: theme.color.danger,
                        }}
                      >
                        {message}
                      </p>
                    )}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Last Name
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <Input {...register("last_name")} isFullWidth />
                  <ErrorMessage
                    errors={errors}
                    name="last_name"
                    render={({ message }) => (
                      <p
                        style={{
                          marginTop: 0,
                          color: theme.color.danger,
                        }}
                      >
                        {message}
                      </p>
                    )}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  CMKL Email
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={3}>
                <>
                  <Input {...register("cmkl_email")} isFullWidth />
                  <ErrorMessage
                    errors={errors}
                    name="cmkl_email"
                    render={({ message }) => (
                      <p
                        style={{
                          marginTop: 0,
                          color: theme.color.danger,
                        }}
                      >
                        {message}
                      </p>
                    )}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <RightAlign>
                  <Button
                    type="button" // Add this line
                    onClick={() => {
                      setOpen?.(false);
                      setEditInstructor(null);
                    }}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 12px" }}
                  >
                    <Icon name="x-s" mr={0.5} />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    onClick={() => handleSubmit(onSubmitClicked)}
                    status={Status.primary}
                    backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginLeft: "8px" }}
                  >
                    <Icon name="floppy" mr={0.5} />
                    Save
                  </Button>
                </RightAlign>
              </FlexGrow>
            </FlexBox>
          </form>
        ) : (
          <Icon mr={1} name="spinner" spin />
        )}
      </Modal>
    </>
  );
};

export default EditInstructorModal;
